import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { occurrenceService, categoryService } from '../services/occurrence.service';
import { Occurrence, Category, STATUS_LABELS, OccurrenceStatus, PRIORITY_LABELS, PRIORITY_COLORS, OccurrencePriority } from '../types';

export default function OccurrencesPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [occurrences, setOccurrences] = useState<Occurrence[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterMine, setFilterMine] = useState<string>('all');

  const activeCityId = user?.activeCityId || user?.primaryCityId || user?.cityId;

  useEffect(() => {
    loadData();
  }, [activeCityId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await occurrenceService.findAll();
      setOccurrences(data);
      if (activeCityId) {
        try {
          const cats = await categoryService.findByCity(activeCityId);
          setCategories(cats);
        } catch {}
      }
    } catch (err) {
      console.error('Failed to load occurrences', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredOccurrences = occurrences
    .filter((occ) => {
      if (filterMine === 'mine' && occ.citizenId !== user?.id) return false;
      if (filterCategory && occ.categoryId !== filterCategory) return false;
      if (filterStatus && occ.status !== filterStatus) return false;
      return true;
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'in_progress': return 'status-in-progress';
      case 'resolved': return 'status-resolved';
      default: return 'status-cancelled';
    }
  };

  if (loading) return <div className="p-8">Carregando...</div>;

  return (
    <>
        <div className="flex flex-wrap gap-2 sm:gap-4 mb-6 items-center">
          <div className="flex rounded-lg bg-gray-100 p-0.5 text-sm">
            <button
              onClick={() => setFilterMine('mine')}
              className={`px-3 py-1.5 rounded-md font-medium transition-colors ${filterMine === 'mine' ? 'bg-white shadow text-primary-700' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Minhas
            </button>
            <button
              onClick={() => setFilterMine('all')}
              className={`px-3 py-1.5 rounded-md font-medium transition-colors ${filterMine === 'all' ? 'bg-white shadow text-primary-700' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Todas
            </button>
          </div>

          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">Todas as categorias</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="block px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">Todos os status</option>
            {Object.entries(STATUS_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50 border-r border-gray-200 z-10">Ações</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prioridade</th>
                {user?.userType !== 'citizen' && <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Solicitante</th>}
                <th className="hidden sm:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Endereço</th>
                <th className="hidden sm:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data / Hora</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOccurrences.map((occ) => (
                  <tr key={occ.id} className="hover:bg-gray-50">
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm sticky left-0 bg-white border-r border-gray-200 z-10">
                      <button
                        onClick={() => navigate(`/occurrences/${occ.id}`)}
                        className="text-primary-600 hover:text-primary-700"
                      >
                        Detalhes
                      </button>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-32 sm:max-w-none truncate">{occ.category?.name || occ.categoryId}</td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                    <span className={getStatusColor(occ.status)}>
                      {STATUS_LABELS[occ.status]}
                    </span>
                  </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      {(user?.userType !== 'citizen' || occ.status !== OccurrenceStatus.PENDING) && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{ backgroundColor: PRIORITY_COLORS[occ.priority ?? OccurrencePriority.MEDIUM] + '20', color: PRIORITY_COLORS[occ.priority ?? OccurrencePriority.MEDIUM], border: `1px solid ${PRIORITY_COLORS[occ.priority ?? OccurrencePriority.MEDIUM]}` }}
                        >
                          {PRIORITY_LABELS[occ.priority ?? OccurrencePriority.MEDIUM]}
                        </span>
                      )}
                    </td>
                  {user?.userType !== 'citizen' && <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-32 truncate">
                    {occ.citizen?.name || '-'}</td>}
                  <td className="hidden sm:table-cell px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-48 truncate">
                    {occ.address || '-'}
                  </td>
                  <td className="hidden sm:table-cell px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(occ.createdAt).toLocaleString('pt-BR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
                </div>
    </>
  );
}

